import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useCart, PRODUCTS } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { db } from "../firebase/config";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import "./Products.css";
import jarHara from "../assets/jar-hara.webp";
import jarTurmeric from "../assets/jar-turmeric.webp";
import jarHoney from "../assets/jar-honey.webp";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

const IMAGES = {
  "hara-namak": jarHara,
  turmeric: jarTurmeric,
  honey: jarHoney,
  "hemp-salt": jarHara,
  "flax-salt": jarHara,
};

const META = {
  "hara-namak": { origin: "Kumaon Hills", note: "The green salt of Pahadi kitchens" },
  turmeric: { origin: "6,000 ft", note: "High-altitude, hand-ground" },
  honey: { origin: "Wild Beehives", note: "Raw, unfiltered, unprocessed" },
  "hemp-salt": { origin: "Himalayan", note: "Protein-rich table companion" },
  "flax-salt": { origin: "Mountain Mills", note: "Nutty, earthy, everyday" },
};

const CATEGORIES = [
  {
    id: "salts",
    name: "Salts",
    description: "Mountain-ground, mineral-rich, no preservatives",
    live: ["hara-namak", "hemp-salt", "flax-salt"],
    coming: [],
  },
  {
    id: "spices",
    name: "Spices",
    description: "Wild-grown, hand-ground, pure by nature",
    live: ["turmeric"],
    coming: [
      { name: "Pahadi Masala", note: "Signature Kumaoni spice blend" },
      { name: "Coriander Powder", note: "Stone-ground, fresh aroma" },
      { name: "Cumin Powder", note: "Hand-roasted mountain jeera" },
    ],
  },
  {
    id: "sweeteners",
    name: "Sweeteners",
    description: "Raw, unheated, nature's best",
    live: ["honey"],
    coming: [
      { name: "Jaggery Powder", note: "Pahadi gur, unrefined" },
    ],
  },
  {
    id: "seeds",
    name: "Seeds & Grains",
    description: "Superfoods from the hills and beyond",
    live: [],
    coming: [
      { name: "Chia Seeds", note: "Omega-3 rich, premium quality" },
      { name: "Amaranth", note: "Pahadi rajgira, protein-packed" },
      { name: "Buckwheat", note: "Kuttu, Uttarakhand staple" },
      { name: "Finger Millet", note: "Pahadi ragi, calcium-rich" },
      { name: "Sattu", note: "Roasted gram, pure energy" },
    ],
  },
  {
    id: "pulses",
    name: "Pulses",
    description: "Heirloom varieties, mountain-grown",
    live: [],
    coming: [
      { name: "Pahadi Rajma", note: "Kumaon's famous red kidney beans" },
      { name: "Black Chana", note: "Mountain-grown, rich in iron" },
      { name: "Masoor Dal", note: "Small batch, stone-cleaned" },
      { name: "Moong Dal", note: "Pure, no polish" },
      { name: "Urad Dal", note: "Whole, natural, traditional" },
    ],
  },
  {
    id: "nuts",
    name: "Nuts & Dried Fruits",
    description: "Uttarakhand's finest, sourced direct",
    live: [],
    coming: [
      { name: "Pahadi Walnuts", note: "Uttarakhand's premium akhrot" },
      { name: "Dried Apricots", note: "Kumaon khubani, sun-dried" },
      { name: "Almonds", note: "Raw, unprocessed, natural" },
      { name: "Dried Buransh Berries", note: "Rhododendron, unique to Uttarakhand" },
    ],
  },
  {
    id: "wellness",
    name: "Wellness",
    description: "Traditional herbs, modern purity standards",
    live: [],
    coming: [
      { name: "Ashwagandha Powder", note: "Adaptogenic, Uttarakhand sourced" },
      { name: "Moringa Powder", note: "Superfood, quality tested" },
      { name: "Amla Powder", note: "Wild Indian gooseberry" },
      { name: "Giloy Powder", note: "Immunity herb, traditional" },
    ],
  },
];

function ComingSoonCard({ product, onClick }) {
  return (
    <div className="product-card" onClick={onClick} style={{ cursor: "pointer" }}>
      <div className="product-image cs-image">
        <div className="cs-text-wrap">
          <span className="cs-text-label">Coming</span>
          <span className="cs-text-soon">Soon</span>
          <span className="cs-text-tag">Proudly Pahadi</span>
        </div>
      </div>
      <div className="product-card-body">
        <div className="product-namerow">
          <h3>{product.name}</h3>
        </div>
        <p className="product-note">{product.note}</p>
        <div className="product-foot">
          <div className="product-actions">
            <button className="btn-add">Notify Me When Available</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Products() {
  const { addToCart, cart } = useCart();
  const { currentUser } = useAuth();
  const [addedId, setAddedId] = useState(null);
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [notifyModal, setNotifyModal] = useState(null);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [notifyStatus, setNotifyStatus] = useState("");
  const [notifyLoading, setNotifyLoading] = useState(false);
  const [pincode, setPincode] = useState("");
  const [pincodeInput, setPincodeInput] = useState("");
  const [edd, setEdd] = useState(null);
  const [eddLoading, setEddLoading] = useState(false);
  const [cityName, setCityName] = useState("");

  const cartHasItems = cart.length > 0;

  function getQty(id) {
    const found = cart.find(i => i.id === id);
    return found ? found.quantity : 0;
  }

  function handleAdd(id) {
    if (addedId) return;
    addToCart(id);
    setAddedId(id);
    setTimeout(() => setAddedId(null), 1100);
  }

  function handleCheckPincode() {
    const clean = pincodeInput.replace(/\D/g, "").slice(0, 6);
    if (clean.length === 6) setPincode(clean);
  }

  React.useEffect(() => {
    if (!pincode || pincode.length !== 6) return;
    setEddLoading(true);
    setCityName("");
    Promise.all([
      fetch(`${BACKEND_URL}/api/edd?pincode=${pincode}&weight=0.3`).then(r => r.json()),
      fetch(`${BACKEND_URL}/api/pincode/${pincode}`).then(r => r.json()),
    ])
      .then(([eddData, pincodeData]) => {
        setEdd(eddData);
        if (pincodeData.valid) setCityName(pincodeData.city);
        setEddLoading(false);
      })
      .catch(() => { setEdd({ label: "4–7 business days" }); setEddLoading(false); });
  }, [pincode]);

  async function handleNotifySubmit() {
    if (!notifyEmail || !notifyEmail.includes("@")) {
      setNotifyStatus("Please enter a valid email.");
      return;
    }
    setNotifyLoading(true);
    try {
      await addDoc(collection(db, "waitlist"), {
        email: notifyEmail,
        product: notifyModal,
        createdAt: serverTimestamp(),
      });
      setNotifyStatus("success");
      setNotifyEmail("");
    } catch (e) {
      setNotifyStatus("Something went wrong. Please try again.");
    } finally {
      setNotifyLoading(false);
    }
  }

  const liveProducts = Object.values(PRODUCTS).filter(p => p.id !== "test-1");

  const filteredLive = liveProducts.filter(p => {
    const matchesCategory = activeCategory === "all" || CATEGORIES.find(c => c.id === activeCategory)?.live?.includes(p.id);
    const matchesSearch = !searchQuery || p.name.toLowerCase().includes(searchQuery.toLowerCase()) || (META[p.id]?.note || "").toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const allComingProducts = CATEGORIES.flatMap(cat => cat.coming.map(p => ({ ...p, category: cat.id })));
  const filteredComing = searchQuery
    ? allComingProducts.filter(p => p.name.toLowerCase().includes(searchQuery.toLowerCase()) || p.note.toLowerCase().includes(searchQuery.toLowerCase()))
    : [];

  const noResults = searchQuery && filteredLive.length === 0 && filteredComing.length === 0;

  return (
    <div className="products-page">
      {/* Hero */}
      <div className="products-hero">
        <div className="page-container">
          <span className="section-label">Our Products</span>
          <h1 className="products-title">Pure from<br /><em>the Mountains</em></h1>
          <p className="products-sub">Every product in our collection has passed the PureJar quality test — sourced from the best, held to the Pahadi standard. Pure by nature, pure by culture.</p>

          {/* EDD checker */}
          <div className="edd-checker">
            <div className="edd-input-row">
              <input
                type="tel"
                placeholder="Enter pincode for delivery estimate"
                value={pincodeInput}
                onChange={e => setPincodeInput(e.target.value.replace(/\D/g, "").slice(0, 6))}
                onKeyDown={e => e.key === "Enter" && handleCheckPincode()}
                className="edd-input"
                maxLength={6}
              />
              <button onClick={handleCheckPincode} className="edd-btn">Check</button>
            </div>
            {eddLoading && <p className="edd-result">Checking delivery...</p>}
            {edd && !eddLoading && (
              <p className="edd-result">
                {cityName && <span style={{ fontWeight: 700 }}>Delivering to {cityName} — </span>}
                {edd.label}{edd.courier && ` via ${edd.courier}`}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="page-container">

        {/* Search bar */}
        <div className="products-search-wrap">
          <div className="products-search-inner">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" style={{ flexShrink: 0, color: "var(--text-mid)" }}>
              <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
            </svg>
            <input
              type="text"
              placeholder="Search products — try 'Honey' or 'Rajma'..."
              value={searchQuery}
              onChange={e => { setSearchQuery(e.target.value); setActiveCategory("all"); }}
              className="products-search-input"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery("")} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-mid)", fontSize: "1rem", padding: "0 4px" }}>✕</button>
            )}
          </div>
        </div>

        {/* Category filter — hidden during search */}
        {!searchQuery && (
          <div className="category-filters">
            <button className={`cat-btn ${activeCategory === "all" ? "active" : ""}`} onClick={() => setActiveCategory("all")}>All</button>
            {CATEGORIES.map(cat => (
              <button key={cat.id} className={`cat-btn ${activeCategory === cat.id ? "active" : ""}`} onClick={() => setActiveCategory(cat.id)}>
                {cat.name}
              </button>
            ))}
          </div>
        )}

        {/* Live products */}
        {filteredLive.length > 0 && (
          <div className="products-grid">
            {filteredLive.map(p => {
              const meta = META[p.id] || {};
              return (
                <div key={p.id} className={`product-card ${p.id === "honey" ? "featured" : ""}`}>
                  {p.id === "honey" && <span className="product-badge">Most Loved</span>}
                  <div className="product-image">
                    <span className="product-origin">{meta.origin}</span>
                    <img src={IMAGES[p.id]} alt={p.name} onClick={() => window.location.href = "/products/" + p.id} style={{ cursor: "pointer" }} />
                  </div>
                  <div className="product-card-body">
                    <div className="product-namerow">
                      <h3 style={{ cursor: "pointer" }} onClick={() => window.location.href = "/products/" + p.id}>{p.name}</h3>
                      <span className="product-weight">{p.weight}g</span>
                    </div>
                    <p className="product-note">{meta.note}</p>
                    <p className="product-desc">{p.description}</p>
                    <div className="product-foot">
                      <div className="product-pricing">
                        <span className="product-price">{"\u20B9" + p.price}</span>
                      </div>
                      {edd && !eddLoading && (
                        <p className="product-edd">{cityName ? `Delivering to ${cityName} by ${edd.label}` : `Delivery by ${edd.label}`}</p>
                      )}
                      <div className="product-actions">
                        <button className={`btn-add ${addedId === p.id ? "btn-added" : ""}`} onClick={() => handleAdd(p.id)} disabled={addedId === p.id}>
                          {addedId === p.id ? <span className="btn-added-content">{"\u2713 " + getQty(p.id) + " in Cart"}</span> : "Add to Cart"}
                        </button>
                        {cartHasItems && <Link to="/cart" className="btn-view-cart">View Cart →</Link>}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Search results for coming soon */}
        {searchQuery && filteredComing.length > 0 && (
          <div>
            <p style={{ color: "var(--text-mid)", fontSize: "0.82rem", marginBottom: 16, marginTop: 8 }}>
              Also coming soon:
            </p>
            <div className="products-grid">
              {filteredComing.map((product, i) => (
                <ComingSoonCard
                  key={i}
                  product={product}
                  onClick={() => { setNotifyModal(product.name); setNotifyStatus(""); setNotifyEmail(""); }}
                />
              ))}
            </div>
          </div>
        )}

        {/* No results */}
        {noResults && (
          <div style={{ textAlign: "center", padding: "48px 20px", color: "var(--text-mid)" }}>
            <p style={{ fontSize: "1.1rem", marginBottom: 8 }}>No products found for "<strong>{searchQuery}</strong>"</p>
            <p style={{ fontSize: "0.88rem" }}>Try Salt, Honey, Turmeric, Rajma or Walnuts</p>
          </div>
        )}

        {/* Coming soon sections — shown when not searching */}
        {!searchQuery && CATEGORIES
          .filter(cat => activeCategory === "all" || activeCategory === cat.id)
          .filter(cat => cat.coming.length > 0)
          .map(cat => (
            <div key={cat.id} className="coming-soon-section">
              <div className="coming-soon-header">
                <div>
                  <h2 className="coming-soon-title">{cat.name}</h2>
                  <p className="coming-soon-desc">{cat.description}</p>
                </div>
              </div>
              <div className="products-grid">
                {cat.coming.map((product, i) => (
                  <ComingSoonCard
                    key={i}
                    product={product}
                    onClick={() => { setNotifyModal(product.name); setNotifyStatus(""); setNotifyEmail(""); }}
                  />
                ))}
              </div>
            </div>
          ))}

        {/* CTA band */}
        <div className="products-cta-band">
          <div className="products-cta-text">
            <h2>Not sure where to start?</h2>
            <p>Use code <strong>WELCOME149</strong> on your first purchase — any salt for just ₹149. Every product, quality tested. Every jar, Proudly Pahadi.</p>
          </div>
          <Link to="/cart" className="btn-outline">View Cart</Link>
        </div>
      </div>

      {/* Notify Me Modal */}
      {notifyModal && (
        <div className="notify-overlay" onClick={() => setNotifyModal(null)}>
          <div className="notify-modal" onClick={e => e.stopPropagation()}>
            <button className="notify-close" onClick={() => setNotifyModal(null)}>✕</button>
            {notifyStatus === "success" ? (
              <div style={{ textAlign: "center", padding: "20px 0" }}>
                <div style={{ fontSize: "2rem", marginBottom: 12 }}>🎉</div>
                <h3 style={{ fontFamily: "'Playfair Display', serif", color: "#2D5016", marginBottom: 8 }}>You're on the list!</h3>
                <p style={{ color: "#4A6030", fontSize: "0.88rem" }}>We'll notify you as soon as <strong>{notifyModal}</strong> is available.</p>
              </div>
            ) : (
              <>
                <div className="cs-text-wrap" style={{ background: "linear-gradient(135deg, #2D5016, #1a3a0a)", borderRadius: 16, padding: "28px 20px", marginBottom: 20 }}>
                  <span className="cs-text-label">Coming</span>
                  <span className="cs-text-soon">Soon</span>
                  <span className="cs-text-tag">Proudly Pahadi</span>
                </div>
                <h3 className="notify-title">Notify me when available</h3>
                <p className="notify-product">{notifyModal}</p>
                <p className="notify-desc">Be the first to know when this product launches. We'll send you one email — no spam.</p>
                <input
                  type="email"
                  placeholder="your@email.com"
                  value={notifyEmail}
                  onChange={e => setNotifyEmail(e.target.value)}
                  className="notify-input"
                />
                {notifyStatus && notifyStatus !== "success" && <p className="notify-error">{notifyStatus}</p>}
                <button className="notify-submit" onClick={handleNotifySubmit} disabled={notifyLoading}>
                  {notifyLoading ? "Saving..." : "Notify Me →"}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}